// Papers (P5, decision 146): vendor invoices and price lists dropped in by the stack, read in the background. One that
// adds up goes into the price and purchase history by itself, never a bill to pay; one that does not waits here with
// the reason. JL: "i dont want to have to check pdfs".
import type { Paper, PaperList, PaperStatus } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { GmailCard } from '../components/GmailCard';
import { OldMailCard } from '../components/OldMailCard';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatStamp } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

const STATUS_LABEL: Record<PaperStatus, AppMessageKey> = { waiting: 'paperWaiting', reading: 'paperReading', posted: 'paperPosted', held: 'paperHeld', skipped: 'paperSkipped', failed: 'paperFailed' };
const STATUS_BADGE: Record<PaperStatus, string> = { waiting: 'badge', reading: 'badge badge-warn', posted: 'badge badge-ok', held: 'badge badge-warn', skipped: 'badge', failed: 'badge badge-danger' };
const ORDER: PaperStatus[] = ['waiting', 'reading', 'posted', 'held', 'skipped', 'failed'];
/** the server lists this many at a time */
const PAGE = 300;

export function PapersPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const [status, setStatusOnly] = useState<PaperStatus | ''>('');
  const [offset, setOffset] = useState(0);
  const setStatus = (s: PaperStatus | '') => {
    setStatusOnly(s);
    setOffset(0);
  };
  const [sending, setSending] = useState<{ n: number; total: number } | null>(null);
  const [sent, setSent] = useState<{ added: number; dupes: number; failed: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const input = useRef<HTMLInputElement>(null);
  const papers = useQuery({
    queryKey: qk.papers(status, offset),
    queryFn: ({ signal }) => api.papers(status || undefined, offset, signal),
    // while papers are being read, the list follows them
    refetchInterval: (q) => {
      const c = (q.state.data as PaperList | undefined)?.counts;
      return c && c.waiting + c.reading > 0 ? 10_000 : false;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['papers'] });
  const retry = useMutation({ mutationFn: (id: string) => api.retryPaper(id), onSuccess: refresh, onError: setError });
  const real = useMutation({ mutationFn: (id: string) => api.trustPaper(id), onSuccess: refresh, onError: setError });
  const pause = useMutation({ mutationFn: (paused: boolean) => api.pausePapers(paused), onSuccess: refresh, onError: setError });
  const retryFailed = useMutation({ mutationFn: () => api.retryFailedPapers(), onSuccess: refresh, onError: setError });
  const keep = useMutation({ mutationFn: (ids: string[]) => api.keepPapers(ids), onSuccess: () => void refresh(), onError: setError });
  const keptSaid = keep.data
    ? t('papersKept')
        .replace('{posted}', String(keep.data.results.filter((r) => r.outcome === 'posted' || r.outcome === 'skipped').length))
        .replace('{held}', String(keep.data.results.filter((r) => r.outcome === 'held').length))
    : null;

  // one at a time: a stack of fifty is fifty small requests, each kept once by its content
  const send = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = [...files];
    const tally = { added: 0, dupes: 0, failed: 0 };
    setError(null);
    setSent(null);
    for (let i = 0; i < list.length; i += 1) {
      setSending({ n: i + 1, total: list.length });
      try {
        const r = await api.addPaper(list[i]!);
        if (r.duplicate) tally.dupes += 1;
        else tally.added += 1;
      } catch (err) {
        tally.failed += 1;
        setError(err);
      }
    }
    setSending(null);
    setSent(tally);
    if (input.current) input.current.value = '';
    void refresh();
  };

  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  const columns: SheetColumn<Paper>[] = [
    { key: 'received', header: t('paperReceivedCol'), width: '116px', render: (p) => formatStamp(p.receivedAt, intlLocale, 'date') },
    { key: 'file', header: t('paperFileCol'), width: '220px', render: (p) => <a href={api.paperFileUrl(p.id)} target="_blank" rel="noreferrer">{p.filename}</a> },
    { key: 'status', header: t('paperStatusCol'), width: '104px', render: (p) => <span className={STATUS_BADGE[p.status]}>{t(STATUS_LABEL[p.status])}</span> },
    { key: 'vendor', header: t('vendor'), width: '140px', render: (p) => p.vendorName ?? '' },
    { key: 'type', header: t('docType'), width: '96px', render: (p) => (p.docType ? t(`doc_${p.docType}` as AppMessageKey) : '') },
    { key: 'date', header: t('dateCol'), width: '116px', render: (p) => (p.docDate ? formatDate(p.docDate, intlLocale, 'date') : '') },
    { key: 'number', header: t('docNoCol'), width: '90px', render: (p) => p.docNumber ?? '' },
    { key: 'lines', header: t('linesCol'), width: '56px', align: 'right', render: (p) => (p.lineCount === null ? '' : String(p.lineCount)) },
    { key: 'total', header: t('totalCol'), width: '90px', align: 'right', render: (p) => money(p.total) },
    { key: 'why', header: t('paperWhyCol'), width: '440px', render: (p) => <span title={p.reason ?? undefined}>{p.reason ?? ''}</span>, cellClass: () => 'wrap' },
    {
      key: 'act',
      header: '',
      width: '110px',
      render: (p) =>
        p.documentId ? (
          <Link to={`/costs/${p.documentId}?k=${p.docType === 'price_list' ? 'lists' : 'bills'}`}>{t('paperOpenDoc')}</Link>
        ) : p.keepable ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={keep.isPending} onClick={() => keep.mutate([p.id])}>
            {t('paperKeep')}
          </button>
        ) : p.status === 'held' && !p.senderVerified ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={real.isPending} onClick={() => real.mutate(p.id)}>
            {t('paperItsReal')}
          </button>
        ) : p.status === 'held' || p.status === 'failed' || p.status === 'skipped' ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={retry.isPending} onClick={() => retry.mutate(p.id)}>
            {t('paperReadAgain')}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="desk-page stack">
      <header className="desk-head">
        <div>
          <h1>{t('papersTitle')}</h1>
          <p className="muted">{t('papersHint')}</p>
        </div>
        {me?.role === 'owner' && papers.data ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={pause.isPending} onClick={() => pause.mutate(!papers.data!.paused)}>
            {papers.data.paused ? t('papersResume') : t('papersPause')}
          </button>
        ) : null}
      </header>

      {me?.role === 'owner' || me?.role === 'office' ? <GmailCard owner={me.role === 'owner'} /> : null}
      {me?.role === 'owner' ? <OldMailCard /> : null}

      <label className="paper-drop">
        <input ref={input} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" disabled={!!sending} onChange={(e) => void send(e.target.files)} />
        <span>{sending ? t('papersUploading').replace('{n}', String(sending.n)).replace('{total}', String(sending.total)) : t('papersDrop')}</span>
      </label>
      {sent ? (
        <p className="muted" role="status">
          {t('papersUploaded').replace('{added}', String(sent.added)).replace('{dupes}', String(sent.dupes)).replace('{failed}', String(sent.failed))}
        </p>
      ) : null}
      {error ? <ErrorBox error={error} /> : null}

      <QueryView query={papers}>
        {(data) => (
          <>
            {!data.readerConfigured ? <p className="alert alert-warn">{t('papersNoReader')}</p> : null}
            {data.paused ? <p className="alert alert-warn">{t('papersPaused')}</p> : null}
            {data.lastError ? <p className="alert alert-error">{t('papersStopped').replace('{why}', data.lastError)}</p> : null}
            <div className="chips" role="group" aria-label={t('paperStatusCol')}>
              <button type="button" aria-pressed={status === ''} className="chip" onClick={() => setStatus('')}>
                {t('papersAll')}
              </button>
              {ORDER.map((s) => (
                <button key={s} type="button" aria-pressed={status === s} className="chip" onClick={() => setStatus(s)}>
                  {t(STATUS_LABEL[s])} <strong>{data.counts[s]}</strong>
                </button>
              ))}
            </div>
            {data.counts.failed > 0 ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={retryFailed.isPending} onClick={() => retryFailed.mutate()}>
                {t('papersRetryFailed')}
              </button>
            ) : null}
            <HeldSummary items={data.items} keep={keep} onDone={() => void refresh()} onError={setError} />
            {keptSaid ? (
              <p className="muted" role="status">
                {keptSaid}
              </p>
            ) : null}
            <p className="muted small">{t('papersCost')}</p>
            {(() => {
              const total = status ? data.counts[status] : ORDER.reduce((n, s) => n + data.counts[s], 0);
              if (total <= PAGE && offset === 0) return null;
              return (
                <div className="row-actions">
                  <button type="button" className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                    {t('papersNewer')}
                  </button>
                  <span className="muted">
                    {t('papersShowing')
                      .replace('{from}', String(Math.min(total, offset + 1)))
                      .replace('{to}', String(Math.min(total, offset + PAGE)))
                      .replace('{total}', String(total))}
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
                    {t('papersOlder')}
                  </button>
                </div>
              );
            })()}
            {data.items.length === 0 ? <Empty>{t('papersNone')}</Empty> : <Sheet label={t('papersTitle')} columns={columns} rows={data.items} rowKey={(p) => p.id} rowLabel={(p) => p.filename} minWidth="1440px" stickyFirst />}
          </>
        )}
      </QueryView>
    </div>
  );
}

type Held = { kind: 'keep' | 'vendor' | 'unit' | 'sender' | 'line' | 'other'; paper: Paper };
const kindOf = (p: Paper): Held['kind'] =>
  p.keepable
    ? 'keep'
    : (p.reason ?? '').startsWith('The vendor was not recognised')
      ? 'vendor'
      : (p.reason ?? '').startsWith('A unit the app does not know')
        ? 'unit'
        : !p.senderVerified && (p.reason ?? '').startsWith('From an address not confirmed')
          ? 'sender'
          : (p.reason ?? '').startsWith('A line does not add up')
            ? 'line'
            : 'other';

/**
 * The papers that need a look, by why, each with what fixes it (decision 151): keep them on your word, name the vendor
 * of a letterhead once, read the misread ones again, check all again for free after a fix.
 */
function HeldSummary({ items, keep, onDone, onError }: { items: Paper[]; keep: { mutate: (ids: string[]) => void; isPending: boolean }; onDone: () => void; onError: (e: unknown) => void }) {
  const { t } = useLocale();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal), staleTime: 10 * 60_000 });
  const [said, setSaid] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const tell = (r: { results: { outcome: string }[] }) => {
    setSaid(t('papersRecheckSaid').replace('{posted}', String(r.results.filter((x) => x.outcome === 'posted' || x.outcome === 'skipped').length)).replace('{held}', String(r.results.filter((x) => x.outcome === 'held').length)));
    onDone();
  };
  const recheck = useMutation({ mutationFn: () => api.recheckPapers(), onSuccess: tell, onError });
  const name = useMutation({ mutationFn: (v: { name: string; vendorId: string }) => api.nameVendor(v.name, v.vendorId), onSuccess: tell, onError });
  const again = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.retryPaper(id);
    },
    onSuccess: onDone,
    onError,
  });
  const held = items.filter((p) => p.status === 'held').map((paper) => ({ kind: kindOf(paper), paper }));
  if (held.length === 0) return null;
  const of = (k: Held['kind']) => held.filter((h) => h.kind === k).map((h) => h.paper);
  const names = [...new Map(of('vendor').map((p) => [p.vendorNameRead ?? '', p])).keys()].filter(Boolean).map((n) => ({ n, count: of('vendor').filter((p) => p.vendorNameRead === n).length }));
  const units = [...new Set(of('unit').map((p) => /"([^"]*)"/.exec(p.reason ?? '')?.[1] ?? '').filter(Boolean))];
  const busy = keep.isPending || recheck.isPending || name.isPending || again.isPending;
  return (
    <section className="held-box stack-sm" aria-label={t('heldTitle')}>
      <div className="row-actions">
        <h2>{t('heldTitle')}</h2>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => recheck.mutate()}>
          {t('papersRecheck')}
        </button>
      </div>
      <p className="muted small">{t('papersRecheckHint')}</p>
      <ul className="held-list">
        {of('keep').length ? (
          <li className="held-row">
            <span className="held-text">
              <strong>{t('heldKeep').replace('{n}', String(of('keep').length))}</strong>
              <span className="muted small">{t('papersKeepHint')}</span>
            </span>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => keep.mutate(of('keep').map((p) => p.id))}>
              {t('papersKeepAll').replace('{n}', String(of('keep').length))}
            </button>
          </li>
        ) : null}
        {names.map(({ n, count }) => (
          <li key={n} className="held-row">
            <span className="held-text">
              <strong>{t('heldVendor').replace('{name}', n).replace('{n}', String(count))}</strong>
              <span className="muted small">{t('heldVendorHint')}</span>
            </span>
            <span className="row-actions">
              <select aria-label={`${t('vendor')}: ${n}`} value={picked[n] ?? ''} onChange={(e) => setPicked((cur) => ({ ...cur, [n]: e.target.value }))}>
                <option value="">{t('vendor')}…</option>
                {(vendors.data ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !picked[n]} onClick={() => name.mutate({ name: n, vendorId: picked[n]! })}>
                {t('heldVendorSave')}
              </button>
            </span>
          </li>
        ))}
        {of('unit').length ? (
          <li className="held-row">
            <span className="held-text">
              <strong>{t('heldUnit').replace('{n}', String(of('unit').length))}</strong>
              <span className="muted small">{t('heldUnitHint').replace('{units}', units.map((u) => `"${u}"`).join(', '))}</span>
            </span>
          </li>
        ) : null}
        {of('sender').length ? (
          <li className="held-row">
            <span className="held-text">
              <strong>{t('heldSender').replace('{n}', String(of('sender').length))}</strong>
              <span className="muted small">{t('heldSenderHint')}</span>
            </span>
          </li>
        ) : null}
        {of('line').length ? (
          <li className="held-row">
            <span className="held-text">
              <strong>{t('heldLine').replace('{n}', String(of('line').length))}</strong>
              <span className="muted small">{t('heldLineHint')}</span>
            </span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => again.mutate(of('line').map((p) => p.id))}>
              {t('heldLineAgain').replace('{n}', String(of('line').length))}
            </button>
          </li>
        ) : null}
        {of('other').length ? (
          <li className="held-row">
            <span className="held-text">
              <strong>{t('heldOther').replace('{n}', String(of('other').length))}</strong>
              <span className="muted small">{t('heldOtherHint')}</span>
            </span>
          </li>
        ) : null}
      </ul>
      {said ? (
        <p className="muted" role="status">
          {said}
        </p>
      ) : null}
    </section>
  );
}
